import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { FormLabel } from './FormLabel';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  icon?: LucideIcon;
  options: SelectOption[];
  value: string | string[];
  onChange: (value: string | string[]) => void;
  className?: string;
  compact?: boolean;
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  placeholder?: string;
}

export function Select({
  icon: Icon,
  options,
  value,
  onChange,
  className = '',
  compact = false,
  label,
  error,
  disabled = false,
  multiple = false,
  placeholder = 'Select...',
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutside = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (!isOpen) {
      return;
    }

    document.addEventListener('mousedown', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
    };
  }, [isOpen]);

  const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
  const selectedLabel = multiple
    ? selectedValues.length > 0
      ? options
          .filter((item) => selectedValues.includes(item.value))
          .map((o) => o.label)
          .join(', ')
      : placeholder
    : (options.find((item) => item.value === value) ?? options[0])?.label || placeholder;

  const handleSelect = (optionValue: string) => {
    if (multiple) {
      const next = selectedValues.includes(optionValue)
        ? selectedValues.filter((v) => v !== optionValue)
        : [...selectedValues, optionValue];
      onChange(next);
    } else {
      onChange(optionValue);
      setIsOpen(false);
    }
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      {label && <FormLabel className="mb-1.5 block nav-label text-gray-700">{label}</FormLabel>}
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) setIsOpen((prev) => !prev);
        }}
        className={`relative inline-flex w-full items-center justify-between gap-3 rounded-2xl border bg-white text-left transition-all duration-200 outline-none shadow-sm group ${
          Icon ? 'pl-11' : 'pl-4'
        } pr-4 py-[13.5px] ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''} ${
          error
            ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-500/5'
            : 'border-gray-200 hover:border-gray-300 focus:border-lime-900 focus:ring-4 focus:ring-lime-900/5'
        }`}
      >
        {Icon && (
          <div
            className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${isOpen ? 'text-lime-900' : 'text-gray-400 group-hover:text-gray-500'}`}
          >
            <Icon size={18} strokeWidth={2.5} />
          </div>
        )}
        <span className="truncate text-sm text-gray-900">{selectedLabel}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && !disabled && (
        <div
          className={
            compact
              ? 'mt-2 max-h-56 overflow-auto rounded-2xl border border-gray-200 bg-white p-2 shadow-xl animate-in fade-in zoom-in-95'
              : 'absolute left-0 right-0 top-full z-[80] mt-2 max-h-64 overflow-auto rounded-2xl border border-gray-200 bg-white p-2 shadow-xl animate-in fade-in zoom-in-95'
          }
        >
          {options.map((option) => {
            const isSelected = selectedValues.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className={`flex w-full items-center justify-between rounded-xl px-3 mb-1 py-3 text-sm transition-colors ${
                  isSelected ? 'bg-lime-50 text-lime-900' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="font-bold text-left">{option.label}</span>
                {isSelected && <Check className="h-4 w-4" />}
              </button>
            );
          })}
        </div>
      )}
      {error && (
        <p className="px-1 mt-1.5 text-xs font-bold text-red-600 animate-in fade-in slide-in-from-top-1">{error}</p>
      )}
    </div>
  );
}
